import { useJsApiLoader } from '@react-google-maps/api';
import { getGeolocation } from '@util/location';
import { useState } from 'react';
import { getGeocode } from 'use-places-autocomplete';

//include streetview
export const GMAPS_LIBRARIES: Array<'drawing' | 'places' | 'geometry' | 'maps' | 'geocoding'> = [
  'maps',
  'places',
  'drawing',
  'geocoding',
];

interface UseLocationToGeocodeReturn {
  geocode: google.maps.GeocoderResult | null;
  convertLocationToGeocode: (address: string) => Promise<google.maps.GeocoderResult | null>;
  isLoaded: boolean;
  pending: boolean;
  geocodeError: boolean;
  geocodeLocation: (lat: number, lng: number) => Promise<google.maps.GeocoderResult[]>;
  geocodeCurrentLocation: () => Promise<google.maps.GeocoderResult[]>;
  setGeocode: (geocode: google.maps.GeocoderResult | null) => void;
}

const useLocationToGeocode = (config: any, locale: string = 'en'): UseLocationToGeocodeReturn => {
  const [geocode, setGeocode] = useState<google.maps.GeocoderResult | null>(null);
  const [pending, setPending] = useState(false);
  const [geocodeError, setGeocodeError] = useState(false);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: config.google.mapKey,
    libraries: GMAPS_LIBRARIES,
  });

  const convertLocationToGeocode = async (
    address: string,
  ): Promise<google.maps.GeocoderResult | null> => {
    if (!address) {
      console.error('No address was provided');
      return null;
    }
    try {
      if (!isLoaded) {
        console.error('Google Maps not loaded yet');
        return null;
      }
      setPending(true);
      console.log(`GEOCODE -- address: ${address}, language: ${locale}`);
      const results = await getGeocode({ address, language: locale });
      if (results[0]) {
        setGeocode(results[0]);
        setGeocodeError(false);
        return results[0];
      }
      throw new Error('No results found');
    } catch (e) {
      console.error('ERROR:', e);
      setGeocodeError(true);
    } finally {
      setPending(false);
    }

    return null;
  };

  const geocodeLocation = async (lat: number, lng: number) => {
    const geocoder = new google.maps.Geocoder();
    const { results } = await geocoder.geocode({
      location: { lat, lng },
    });
    return results;
  };

  const geocodeCurrentLocation = async (): Promise<google.maps.GeocoderResult[]> => {
    try {
      setPending(true);
      const { latitude, longitude } = await getGeolocation();
      return geocodeLocation(latitude, longitude);
    } finally {
      setPending(false);
    }
  };

  return {
    geocode,
    convertLocationToGeocode,
    geocodeLocation,
    geocodeCurrentLocation,
    pending,
    isLoaded,
    geocodeError,
    setGeocode,
  };
};

export default useLocationToGeocode;
